import { message, Modal } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useNavigate } from "react-router";
import apiCall from "../../../components/Api";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFIlterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import StatusComponent from "../../../components/Common/StatusComponent";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";
import { IoCloseCircleOutline } from "react-icons/io5";

// Remove filters if not needed, but keeping structure intact
const emptyFilterValues = {
  "Lead Status": [],
  "Registered At": [],
};

// Helper: Strip HTML tags for preview
const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function FranchiseFAQMasters() {
  const navigate = useNavigate();

  // Sorting
  const [sort, setSort] = useState("newest");
  const [sortLabel, setSortLabel] = useState("Sort by");
  const handleSortClick = useSortHandler(setSort, setSortLabel);

  // Search
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearchText, () => {
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
  });

  // Filters
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(emptyFilterValues);
  const [activeFilterCategory, setActiveFilterCategory] =
    useState("Lead Status");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilterValues);

  // Delete Modal
  const [deleteModalState, setDeleteModalState] = useState({
    id: "",
    status: false,
  });

  const [isLoading, setIsLoading] = useState(true);

  // Eye Modal
  const [answerModal, setAnswerModal] = useState({
    open: false,
    content: "",
    question: "",
  });

  // Pagination — FAQ Masters API
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}healthsy-pharmacy/faq-masters/list`,
      initialParams: {
        sort,
        searchText,
        selectedValues: selectedFilters,
      },
    });

  useEffect(() => {
    fetchData({
      sort,
      searchText,
      selectedValues: selectedFilters,
      skip: pagination.skip,
      limit: pagination.limit,
    });
  }, [sort, searchText, selectedFilters, pagination.skip, pagination.limit]);

  const deleteHandler = useDeleteHandler(
    "healthsy-pharmacy/faq-masters",
    fetchData,
    setDeleteModalState
  );

  const handleDelete = async (id) => {
    try {
      await deleteHandler(id);
      setDeleteModalState({ id: "", open: false });
      message.success("FAQ deleted successfully");
    } catch (error) {
      setDeleteModalState({ id: "", open: false });
      message.error("Failed to delete FAQ");
    }
  };

  const handleEdit = (id) => {
    navigate(`/franchise-customers/faq-masters/form/${id}`);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await apiCall(
        `healthsy-pharmacy/faq-masters/update/${id}`,
        "PATCH",
        { active: !currentStatus },
        null
      );
      fetchData();
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  
  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: 60,
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
    },
    {
      title: "FAQ Question",
      dataIndex: "faq_question",
      key: "faq_question",
      width: 300,
    },
    {
      title: "FAQ Answer",
      dataIndex: "faq_answer",
      key: "faq_answer",
      width: 500,
      render: (text, record) => {
        const plain = stripHtml(text || "");
        if (plain.length > 30) {
          return (
            <span className="d-flex align-items-center gap-1">
              {plain.substring(0, 50) + "..."}{" "}
              <span
                onClick={() =>
                  setAnswerModal({
                    open: true,
                    content: text,
                    question: record.faq_question,
                  })
                }
                style={{
                  backgroundColor: "#F7F7F7",
                  cursor: "pointer",
                  borderRadius: 6,
                  padding: 4,
                }}
              >
                <span className="d-flex align-items-center gap-2">
                  View
                  <MdOutlineRemoveRedEye />
                </span>
              </span>
              {/* <EyeOutlined
                style={{ cursor: "pointer", color: "red" }}
                onClick={() =>
                  setAnswerModal({
                    open: true,
                    content: text,
                    question: record.faq_question,
                  })
                }
              /> */}
            </span>
          );
        }
        return plain;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => formatDate(text),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (text) => formatDate(text),
    },
    {
      title: "Status & Actions",
      width: 250,
      fixed: "right",
      render: (text, record) => (
        <StatusComponent
          record={record}
          handleEdit={handleEdit}
          setDeleteModal={setDeleteModalState}
          handleActive={(id) => handleToggleActive(id, record.active)}
          title="faq_masters"
        />
      ),
    },
  ];

  const handlePageChange = usePageChange(pagination, setPagination);

  // Filters - optional
  const fetchAndMap = async (url, mapFn) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}${url}`,
        { noLimit: true }
      );
      return data?.rows?.map(mapFn) || [];
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      return [];
    }
  };

  useEffect(() => {
    if (records) setTimeout(() => setIsLoading(false));
  }, [records]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const leadStatuses = await fetchAndMap(
        "lead-status/list",
        (lead) => lead?.type
      );
      setLeadStatusOptions(leadStatuses);
    };
    fetchFilterOptions();
  }, []);

  const filters = useMemo(
    () => ({
      "Lead Status": [],
      "Registered At": [],
    }),
    [leadStatusOptions]
  );

  const addButtonCfg = {
    id: "addnewfaq",
    onClick: () => navigate("/franchise-programme/faq-masters/form"),
    style: { width: "240px" },
    label: "Add New FAQ",
  };

  return (
    <>
      <ListPageLayout
        breadcrumbs={
          <AntdBreadcrumbs
            className="healthsyMasterBreadCrumbs pb-0"
            breadcrumbsList={[
              { text: "Franchise programme", link: "" },
              { text: "FAQ Masters", link: "" },
            ]}
          />
        }
        addButton={<FilledButton props={addButtonCfg} />}
        titles={<></>}
        filters={
          <DoctorsFilterOptions
            limit={pagination.limit}
            sortName={sortLabel}
            search={searchText}
            showFilter={false}
            onChange={debouncedSearch}
            setLimit={(val) =>
              setPagination((prev) => ({
                ...prev,
                limit: val,
                skip: 0,
                currentPage: 1,
              }))
            }
            downloadExcel={false}
            handleSortClick={handleSortClick}
            showSortBy={true}
          />
        }
        tableProps={{
          columns,
          dataSource: records,
          className: "mx-3 pt-2 table-scrollable medicineList_table",
        }}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={(selected) => handlePageChange({ selected })}
        loader={isLoading}
      >
        {deleteModalState.status && (
          <Deletepopup
            handleDelete={handleDelete}
            id={deleteModalState?.id}
            isCloseModalEnable={true}
            handleClose={() => setDeleteModalState({ id: "", open: false })}
          />
        )}

        {/* Eye Modal for full answer */}
        <Modal
          title={answerModal.question || "FAQ Answer"}
          open={answerModal.open}
          footer={null}
          onCancel={() =>
            setAnswerModal({ open: false, content: "", question: "" })
          }
          // style={{
          //   maxHeight: 400,
          //   overflowY: "auto",
          // }}
          closeIcon={<IoCloseCircleOutline size={24} />}
          centered
          maskClosable={false}
        >
          <div
            style={{
              maxHeight: 550,
              overflowY: "auto",
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: answerModal.content }}
              style={{ whiteSpace: "pre-line" }}
            />
          </div>
        </Modal>

        <CommonFIlterModal
          visible={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={async () => {
            setSelectedFilters(tempFilters);
            setIsFilterModalOpen(false);
            setPagination((prev) => ({
              ...prev,
              skip: 0,
              currentPage: 1,
            }));
          }}
          onReset={() => {
            setSelectedFilters(emptyFilterValues);
            setTempFilters(emptyFilterValues);
            setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
            fetchData();
          }}
          filters={filters}
          selectedCategory={activeFilterCategory}
          setSelectedCategory={setActiveFilterCategory}
          selectedValues={tempFilters}
          setSelectedValues={setTempFilters}
        />
      </ListPageLayout>
    </>
  );
}
